import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconGift, IconMinus, IconPlus, IconStar } from "@tabler/icons-react";
import { AvailableLoyaltyReward } from "../../types/loyalty";
import { formatCurrency } from "../../utils/cashDrawerUtils";
import styles from "./LoyaltyRewardPickerModal.module.css";

/** A pick coming back to the parent: one entry per reward with quantity > 0. */
export interface LoyaltyRewardPick {
  reward: AvailableLoyaltyReward;
  quantity: number;
}

interface LoyaltyRewardPickerModalProps {
  opened: boolean;
  onClose: () => void;
  /** All rewards the client can afford (typically the getTransactionAvailableLoyaltyRewards result). */
  rewards: AvailableLoyaltyReward[];
  /** Total loyalty points the client currently has (before staging). */
  pointsBalance: number;
  /** Currently applied picks (used to seed the stepper counts when reopened). */
  initialPicks?: LoyaltyRewardPick[];
  /** Fired when the user clicks Apply — one entry per reward with quantity > 0. */
  onApply: (picks: LoyaltyRewardPick[]) => void;
}

function num(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * "Choose one offer or reward" modal.
 *
 * Each reward card carries a `[− N +]` stepper. Selection = quantity > 0
 * (blue border + light-blue fill). `+` disables when adding another would
 * exceed the client's total point balance across ALL cards.
 *
 * The modal maintains draft state internally; nothing publishes upstream
 * until Apply. Cancel discards the draft and closes.
 */
export default function LoyaltyRewardPickerModal({
  opened,
  onClose,
  rewards,
  pointsBalance,
  initialPicks = [],
  onApply,
}: LoyaltyRewardPickerModalProps) {
  // Draft quantities keyed by reward_id — reset every time the modal opens so
  // reopens reflect the currently-applied state (initialPicks) and any
  // in-modal edits from a prior session don't persist across close.
  const [draftByRewardId, setDraftByRewardId] = useState<
    Record<string, number>
  >({});

  useEffect(() => {
    if (!opened) return;
    const seed: Record<string, number> = {};
    initialPicks.forEach((pick) => {
      seed[String(pick.reward.reward_id)] = pick.quantity;
    });
    setDraftByRewardId(seed);
    // Effect intentionally depends only on `opened`: reseeding on
    // initialPicks identity churn while the modal is open would clobber the
    // operator's in-modal edits. (CRA base ESLint doesn't load
    // react-hooks/exhaustive-deps, so no disable comment is needed.)
  }, [opened]);

  // Running totals across the draft.
  const { totalPoints, totalDollars } = useMemo(() => {
    let points = 0;
    let dollars = 0;
    rewards.forEach((reward) => {
      const qty = draftByRewardId[String(reward.reward_id)] ?? 0;
      points += qty * num(reward.points_cost);
      dollars += qty * num(reward.applied_value);
    });
    return { totalPoints: points, totalDollars: dollars };
  }, [draftByRewardId, rewards]);

  const remainingPoints = Math.max(0, pointsBalance - totalPoints);

  const setQuantity = useCallback((rewardId: string | number, next: number) => {
    setDraftByRewardId((current) => ({
      ...current,
      [String(rewardId)]: Math.max(0, next),
    }));
  }, []);

  // Editable stepper count — clicking the number swaps it out for a text
  // input so the operator can type any whole quantity. On blur / Enter we
  // parse + clamp against the client's remaining balance for THAT reward
  // (accounting for other rewards' staged spend) so a bad number never
  // pushes the total over the balance. Escape reverts the edit.
  const [editingRewardId, setEditingRewardId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState<string>("");

  const startEditing = useCallback((rewardId: string, currentQty: number) => {
    setEditingRewardId(rewardId);
    setEditingDraft(String(currentQty));
  }, []);

  const cancelEditing = useCallback(() => {
    setEditingRewardId(null);
    setEditingDraft("");
  }, []);

  const commitEditing = useCallback(
    (rewardId: string, cost: number, otherStagedPoints: number) => {
      const parsed = Math.floor(Number(editingDraft));
      if (Number.isFinite(parsed) && parsed >= 0) {
        // Max qty for THIS reward = points remaining after OTHER staged
        // rewards, divided by this reward's cost.
        const remaining = Math.max(0, pointsBalance - otherStagedPoints);
        const maxQty = cost > 0 ? Math.floor(remaining / cost) : 0;
        setQuantity(rewardId, Math.min(parsed, maxQty));
      }
      cancelEditing();
    },
    [editingDraft, pointsBalance, setQuantity, cancelEditing],
  );

  const handleApply = useCallback(() => {
    const picks: LoyaltyRewardPick[] = [];
    rewards.forEach((reward) => {
      const qty = draftByRewardId[String(reward.reward_id)] ?? 0;
      if (qty > 0) picks.push({ reward, quantity: qty });
    });
    onApply(picks);
    onClose();
  }, [rewards, draftByRewardId, onApply, onClose]);

  const header = (
    <Box className={styles.headerRow}>
      <Box component="span" className={styles.headerTitle}>
        Choose One Offer or Reward
      </Box>
      <Box
        component="span"
        className={styles.pointsPill}
        aria-label="Points balance"
      >
        <IconStar size={13} />
        {pointsBalance.toLocaleString("en-US")} pts
      </Box>
    </Box>
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton
      centered
      size="lg"
      padding="lg"
      title={header}
      styles={{
        title: { flex: 1 },
        // padding: top 20 / right 15 / bottom 0 / left 20 per
        // 2026-08-26 feedback (kills the empty gap between the title
        // and the underline bar).
        header: { alignItems: "flex-start", padding: "20px 15px 0 20px" },
      }}
    >
      <Box className={styles.eyebrow}>Available to you</Box>

      {rewards.length === 0 ? (
        <Box className={styles.emptyState}>
          No rewards available for this transaction.
        </Box>
      ) : (
        <Stack gap="sm">
          {rewards.map((reward) => {
            const key = String(reward.reward_id);
            const qty = draftByRewardId[key] ?? 0;
            const cost = num(reward.points_cost);
            const applied = num(reward.applied_value);
            const selected = qty > 0;
            // Disable + when one more pick would push total point spend
            // above the client's balance.
            const overBalance = totalPoints + cost > pointsBalance + 1e-9;
            const canIncrement = !overBalance;
            const incrementDisabledReason = overBalance
              ? "Not enough remaining points."
              : undefined;

            return (
              <Box
                key={key}
                className={`${styles.rewardCard} ${
                  selected ? styles.rewardCardSelected : ""
                }`}
                data-selected={selected || undefined}
              >
                <Box className={styles.rewardIconTile}>
                  <IconGift size={22} stroke={1.8} />
                </Box>
                <Box className={styles.rewardBody}>
                  <Box component="span" className={styles.rewardCost}>
                    {cost.toLocaleString("en-US")} pts
                  </Box>
                  <Box component="span" className={styles.rewardName}>
                    {reward.name}
                  </Box>
                  <Box component="span" className={styles.rewardSubtitle}>
                    Loyalty reward · {formatCurrency(applied)} off
                  </Box>
                </Box>
                <Box className={styles.stepper}>
                  <ActionIcon
                    variant="light"
                    color="var(--brand-teal)"
                    radius="xl"
                    size="lg"
                    aria-label={`Remove one ${reward.name}`}
                    onClick={() => setQuantity(reward.reward_id, qty - 1)}
                    disabled={qty <= 0}
                  >
                    <IconMinus size={14} />
                  </ActionIcon>
                  {/* Always an <input>, never a click-to-edit span — the
                      visual affordance tells the operator they can type the
                      quantity directly. `editingRewardId` still tracks which
                      row is being typed into so the value shows the raw
                      keystrokes; clamping runs on blur / Enter. */}
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={editingRewardId === key ? editingDraft : String(qty)}
                    onFocus={() => startEditing(key, qty)}
                    onChange={(e) => {
                      // Strip non-digits so the field stays numeric even
                      // when someone pastes.
                      const digitsOnly = e.currentTarget.value.replace(
                        /[^0-9]/g,
                        "",
                      );
                      setEditingDraft(digitsOnly);
                    }}
                    onBlur={() =>
                      commitEditing(key, cost, totalPoints - qty * cost)
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.currentTarget.blur();
                      } else if (e.key === "Escape") {
                        cancelEditing();
                      }
                    }}
                    className={styles.stepperInput}
                    aria-label={`${reward.name} quantity`}
                  />
                  <Tooltip
                    label={incrementDisabledReason}
                    disabled={!incrementDisabledReason}
                    withArrow
                  >
                    <Box>
                      <ActionIcon
                        variant="light"
                        color="var(--brand-teal)"
                        radius="xl"
                        size="lg"
                        aria-label={`Add one ${reward.name}`}
                        onClick={() => setQuantity(reward.reward_id, qty + 1)}
                        disabled={!canIncrement}
                      >
                        <IconPlus size={14} />
                      </ActionIcon>
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}

      <Box className={styles.footer}>
        <Box className={styles.footerSummary}>
          {totalPoints > 0 && (
            <Box
              component="span"
              className={styles.footerSummaryLine}
              data-testid="footer-summary-line"
            >
              {`Total points redeemed: ${totalPoints.toLocaleString(
                "en-US",
              )} pts → ${formatCurrency(totalDollars)}`}
            </Box>
          )}
        </Box>
        <Group className={styles.footerActions}>
          <Button variant="subtle" color="gray" onClick={onClose}>
            Cancel
          </Button>
          <Button color="var(--brand-teal)" onClick={handleApply}>
            Apply
          </Button>
        </Group>
      </Box>
    </Modal>
  );
}
